import { NGXLogger as LoggerService } from "ngx-logger";
import type { EssSoftwareUpdateService } from 'ngx-essentia';

import { BannerInterface } from "./banner.interface";
import { TranslationGeneralService } from "../translation/translation.general.service";
import { ConnectionStatusService } from "../services/connection-status.service";
import { RouteContextService } from "../services/route-context.service";
import { DB_ROUTE_IDS } from "../services/connection-stalled-reporter";

/**
 * Hint with a reload button when the database connection does not come up (workorder
 * app-check-connection-stalled-hint-2026-10-26). Shown while `ConnectionStatusService.stalledLong$` (30 s, since
 * workorder app-check-connection-stalled-telemetry-2026-10-27) is true on a route with database context, until the
 * connection is confirmed or the user taps "Later" (session only, no localStorage - the next visit shows it again).
 * "Reload" first activates a waiting service-worker update, so an old build without App Check reaches the new one
 * with one tap, then reloads. Priority above the anonymous (101) and the sample banner (110). The
 * `ConnectionStalled:` reports are ConnectionStalledReporter's job (shared/services/connection-stalled-reporter.ts) -
 * they go out after 10 s whether the banner shows or not.
 */
export class ConnectionStalledBanner implements BannerInterface {
    public priority = 200;
    public icon = 'cloud_off';
    public message: string;
    public leftButtonLabel: string;
    public rightButtonLabel: string;
    private stalledLong = false;
    private dismissed = false;

    constructor(
        private logger: LoggerService,
        private connectionStatus: Pick<ConnectionStatusService, 'stalledLong$'>,
        private routeContext: Pick<RouteContextService, 'id'>,
        private trans: Pick<TranslationGeneralService, 'translate'>,
        private softwareUpdate: Pick<EssSoftwareUpdateService, 'reloadWithSwUpdate'>,
    ) {
        this.message = this.trans.translate('connectionStalled.message');
        this.leftButtonLabel = this.trans.translate('connectionStalled.leftButtonLabel');
        this.rightButtonLabel = this.trans.translate('connectionStalled.rightButtonLabel');
        this.connectionStatus.stalledLong$.subscribe(stalledLong => { this.stalledLong = stalledLong; });
    }

    getShowBanner(): boolean {
        return this.stalledLong && !this.dismissed && DB_ROUTE_IDS.has(this.routeContext.id);
    }

    /** "Later": hidden for this session (the BannerService also removes the banner from its queue) */
    public handleLeftButtonClick(): void {
        this.dismissed = true;
        this.logger.log('ConnectionStalledBanner: dismissed for this session');
    }

    /** "Reload": activate a waiting service-worker update first, then reload */
    public handleRightButtonClick(): void {
        this.logger.log('ConnectionStalledBanner: reload requested');
        void this.softwareUpdate.reloadWithSwUpdate();
    }
}
